import { z } from 'zod';

const queryStringToBoolean = (val: unknown): boolean => {
  if (val === undefined || val === null || val === '') {
    return false;
  }
  const s = String(val).toLowerCase().trim();
  return s === 'true' || s === '1' || s === 'yes';
};

const queryStringToYearMo = (val: unknown): unknown => {
  if (val === '' || val === undefined || val === null) {
    return undefined;
  }
  if (typeof val === 'number' && Number.isInteger(val)) {
    return val;
  }
  const s = String(val).trim();
  if (s === '') {
    return undefined;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
};

/** Calendar month (`YYYYMM`, month 01–12) for BH attendance query scope. */
const tvDashboardYearMoSchema = z
  .number()
  .int()
  .min(200001)
  .max(209912)
  .refine(n => {
    const month = n % 100;
    return month >= 1 && month <= 12;
  }, 'yearMo must be a valid calendar month (YYYYMM)');

/**
 * Query string for `GET /tv-dashboard` BH attendance — `token`, optional `forceReload`
 * to bypass Redis, optional `yearMo` as `YYYYMM` month to query (defaults to current UTC month server-side).
 */
export const TvDashboardBhAttendanceQuerySchema = z.object({
  token: z.preprocess(
    val => (val === '' ? undefined : val),
    z.string({ required_error: 'token is required' }).min(1, 'token is required')
  ),
  forceReload: z.preprocess(queryStringToBoolean, z.boolean()).optional().default(false),
  yearMo: z.preprocess(
    queryStringToYearMo,
    tvDashboardYearMoSchema.optional()
  ),
});

/** Validated query for {@link TvDashboardBhAttendanceQuerySchema}. */
export type TvDashboardBhAttendanceQueryDto = z.infer<
  typeof TvDashboardBhAttendanceQuerySchema
>;

/**
 * One calendar day of rolled-up BH attendance from the TV dashboard API
 * (`GET` with `token` query).
 */
export interface BhAttendanceDto {
  /** ISO date `YYYY-MM-DD`. */
  fullDt: string;
  queuedInMonthDenominator: number;
  daysReemaBhAttendanceTarget: number;
  cntBhAttended: number;
  /**
   * Share of the queued cohort attended: `cntBhAttended / queuedInMonthDenominator × 100`
   * (one decimal), or `0` when the denominator is zero.
   */
  shareAttendingPercent: number;
}
